import { Component, OnInit, HostListener, Inject } from "@angular/core";
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { NgxSpinnerService } from "ngx-spinner";
import { AuthService } from "../../share/service/auth.service";
import { UserDataService } from "src/app/share/data-service/user.data.service";
import { Router } from "@angular/router";

// Lock import
import { timer, Subscription } from 'rxjs';
import { AccountLockService } from 'src/app/share/service/password-services/account-lock.service'
import { PasswordPolicyDto } from 'src/app/share/interface/password.interface';
import { PasswordPolicyService } from "src/app/share/service/password-services/password-policy.service";

// Password Updater Import
import { MatDialog } from '@angular/material/dialog';
import { PasswordExpiryDialogComponent } from 'src/app/share/dialog/password-expiry-dialog/password-expiry-dialog.component';
import { PasswordCheckService } from "src/app/share/service/password-services/password-check.service";

// Missing Import after merge
import { AlertService } from "src/app/share/service/alert.service"
import { UserService } from "src/app/share/service/user.service";

@Component({
	selector: "app-login",
	templateUrl: "./login.component.html",
	styleUrls: ["./login.component.scss"]
   
})
export class LoginComponent {
  formGroup: UntypedFormGroup;
  hide: boolean = true;

	// Take in from back end
	lockDuration: number | null; // In minutes
	lastUpdate: Date = new Date();

	// Potential future number of login attempts policy
	// For now, to update number of login attempts just change this number
	failureSetting: number = 5;

	// Lock attributed variables
	failureCount: number = 0;
	accountLocked: boolean = false;
	remainingAttempts: number = this.failureSetting
	unlockTime: Date | null = null;
	countdown: string = '';
	private timerSubscription: Subscription | null = null;

	// Password Update attributed variables
	// REMOVE BEFORE DEPLOYMENT
	passwordExpiryDays: number = 90;

	userId: string | null = null;
	userEmail: string | null = null;

	constructor(
		private authService: AuthService,
		private userService: UserService,
		private userDataService: UserDataService,
		private passwordCheckService: PasswordCheckService,
		private ngxSpinnerService: NgxSpinnerService,
		private alertService: AlertService,
		private dialog: MatDialog,
		private router: Router,
		private accountLockService: AccountLockService,
		private passwordPolicyService: PasswordPolicyService,
	) {}

	ngOnInit(): void {
		this.formGroup = new FormGroup({
			email: new FormControl(null, [Validators.required, Validators.email]),
			password: new FormControl(null, [Validators.required]),
		});

	    this.initializeLockDuration();
	}

	ngOnDestroy(): void {
		this.clearTimer();
	}

	// Get the lock duration from the PasswordPolicies ta
	private initializeLockDuration(): void {
		this.passwordPolicyService.getActivePolicy().subscribe({
			next: (policy: PasswordPolicyDto) => {
				this.lockDuration = policy.lockDuration;
			},
			error: (err) => {
				console.error('Failed to load password policies', err);

				this.lockDuration = 15;
			}
		});
	}

	async login(): Promise<void> {
		if (this.formGroup.invalid) return;

		const value = this.formGroup.value;
		this.ngxSpinnerService.show();
		this.userEmail = value.email;

		try {
			const lockStatus = await this.accountLockService.getLockStatusByEmail(value.email);

			if (lockStatus.lockType === 'Temporary' && this.lockDuration !== null) {
				this.accountLocked = true;
				this.unlockTime = new Date(lockStatus.unlockTime!);
				this.startCountdown();
				this.ngxSpinnerService.hide();
				this.alertService.openSnackBar("Account temporarily locked.");
				return;
			}

			if (lockStatus.lockType === 'Permanent' && this.lockDuration !== null) {
				this.accountLocked = true;
				this.ngxSpinnerService.hide();
				this.alertService.openSnackBar("Account permanently locked.");
				return;
			}

			const token = await this.userDataService.login(value.email, value.password);
			this.authService.setToken(token);

			const profile = await this.userDataService.getProfile();
			this.userId = profile.id;

			const rights = await this.userDataService.getUserAccessRights();

			this.userService.setToken(rights);

			this.ngxSpinnerService.hide();

			this.checkPasswordAge();
		} catch (err: any) {
			this.handleFailedLogin(value.email);
			this.alertService.openSnackBar(err?.error || "Login failed", "DISMISS");
		}
	}

	// Checks password age and decides if the user needs to update password or not
	private async checkPasswordAge(): Promise<void> {
		const isPasswordRecent = await this.passwordCheckService.checkPasswordAge(this.userId);

		if (!isPasswordRecent) {
			this.showPasswordUpdateDialog();
		} else {
			this.router.navigate(['/settings/myaccount']);
		}
	}

	// Shows the update dialog
	private async showPasswordUpdateDialog(): Promise<void> {
		try {
			const userProfile = await this.userDataService.getProfile();

			const dialogRef = this.dialog.open(PasswordExpiryDialogComponent, {
				width: '450px',
				maxWidth: '90vw',
				panelClass: 'centered-dialog',
				disableClose: true,
				data: { user: userProfile }
			});

			dialogRef.afterClosed().subscribe((result) => {
				if (result?.success) {
					this.router.navigate(['/settings/myaccount']);
				} else {
					this.authService.logout();
					location.reload();
				}
			});
		} catch (err) {
			this.authService.logout();
			location.reload();
		}
	}

	@HostListener('window:beforeunload', ['$event'])
	beforeUnloadHandler(event:Event) {
		if (this.dialog.openDialogs.length > 0) {
			event.preventDefault();
			(event as BeforeUnloadEvent).returnValue = 'You have unsaved changes in the password dialog';
		}
	}

	// Counts up the number of login attempts and the number of remaining attempts
	handleFailedLogin(email: string): void {

		if (this.lockDuration === null) {
			return;
		}

		this.failureCount++;

		const remaining = this.failureSetting - this.failureCount;
		this.remainingAttempts = remaining;

		if (remaining <= 0) {
			// Get the current locktime policy
			const lockDuration = this.lockDuration;

			this.accountLockService.addTempLock(email, lockDuration).subscribe({
				next: () => {
					this.accountLocked = true;
					this.unlockTime = new Date(Date.now() + lockDuration * 60000);
					this.startCountdown();
					this.alertService.openSnackBar('Account temporarily locked due to failed attempts.', 'DISMISS');
				},
				error: () => {
					this.alertService.openSnackBar('Failed to apply lock. Please contact support.', 'DISMISS');
				},
			});
		} else {
			this.alertService.openSnackBar(`Login failed. ${remaining} attempts remaining.`, 'DISMISS');
		}
	}

	// Account lock countdown
	private startCountdown(): void {
		this.clearTimer();

		this.timerSubscription = timer(0, 1000).subscribe(() => {
			if (!this.unlockTime) {
				this.clearTimer();
				return;
			}

			const now = new Date();
			const diffMs = this.unlockTime.getTime() - now.getTime();

			if (diffMs <= 0) {
				this.resetLoginAttempts();
				return;
			}

			const diffMins = Math.floor(diffMs / 60000);
			const diffSecs = Math.floor((diffMs % 60000) / 1000);

			this.countdown = `${diffMins}:${diffSecs < 10 ? '0' + diffSecs : diffSecs}`;
		});
	}

	// Account lock timer reset
	private clearTimer() {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
			this.timerSubscription = null;
		}
	}

	// Resets login attempts
	private async resetLoginAttempts(): Promise<void> {
		try {
			const lockStatus = await this.accountLockService.getLockStatusByEmail(this.userEmail);

			if (lockStatus.lockType === 'None') {
				this.failureCount = 0;
				this.remainingAttempts = this.failureSetting;
				this.accountLocked = false;
				this.unlockTime = null;
				this.countdown = '';
				this.clearTimer();
			} else if (lockStatus.lockType === 'Temporary') {
				// Lock still active
				this.unlockTime = new Date(lockStatus.unlockTime!);
				this.startCountdown();
			}
		} catch (err) {
			this.alertService.openSnackBar("Failed to refresh lock status", "DISMISS");
		}
	}
}
